<template>
  <div class="row form-row">
    <div class="col-sm-4 mb-2">
      <auto-complete
        :key="`${yearOptions}`"
        rules="required"
        :label="$t('message.trade.vehicleInfo.year')"
        :placeholder="$t('message.trade.vehicleInfo.selectYear')"
        :disabled="yearOptions.length === 0 || disableSelectorValues"
        :hiddenLabel="false"
        :value="year"
        :sourceData="yearOptions"
        @onSelect="handleYearSelection"
        id="yearId"
        vid="yearId"
        data-cy="year"
      >
      </auto-complete>
    </div>
    <div class="col-sm-8 mb-2">
      <auto-complete
        :key="`${makeOptions}`"
        rules="required"
        :label="$t('message.trade.vehicleInfo.make')"
        :placeholder="$t('message.trade.vehicleInfo.selectMake')"
        :disabled="makeOptions.length === 0 || disableSelectorValues"
        :hiddenLabel="false"
        :value="make"
        :sourceData="makeOptions"
        @onSelect="handleMakeSelection"
        id="makeId"
        vid="makeId"
        data-cy="make"
      >
      </auto-complete>
    </div>
    <div class="col-12 mb-2">
      <auto-complete
        :key="`${modelOptions}`"
        rules="required"
        :label="$t('message.trade.vehicleInfo.model')"
        :placeholder="$t('message.trade.vehicleInfo.selectModel')"
        :disabled="modelOptions.length === 0 || disableSelectorValues"
        :hiddenLabel="false"
        :value="model"
        @onSelect="handleModelSelection"
        :sourceData="modelOptions"
        id="modelId"
        vid="modelId"
        data-cy="model"
      >
      </auto-complete>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState, mapMutations } from 'vuex';
import log from '@/services/log';
import AutoComplete from '@/components/shared/AutoComplete.vue';

export default {
  components: { AutoComplete },
  computed: {
    ...mapState({
      year: state => state.trade.vehicle.year,
      make: state => state.trade.vehicle.make,
      model: state => state.trade.vehicle.model,
      vehicle: state => state.trade.vehicle,
    }),

    ...mapGetters({
      rawYearOptions: 'trade/getYearOptions',
      makeOptions: 'trade/getMakeOptions',
      modelOptions: 'trade/getModelOptions',
    }),

    yearOptions() {
      return this.rawYearOptions.map(year => String(year));
    },
  },
  props: {
    disableSelectorValues: {
      type: Boolean,
      default: false,
    },
  },
  async created() {
    try {
      await this.fetchYears();
      if (this.vehicle && this.year !== '' && this.make !== '' && this.model !== '') {
        await this.fetchMakeList().then(() => this.fetchModelList());
      }
    } catch (error) {
      log('Error in Selection Year', error);
      this.$router.push({ path: `${this.getProductBasePath()}/error` });
    }
  },
  methods: {
    ...mapActions('trade', {
      fetchYears: 'getYears',
      fetchMakes: 'getMakes',
      fetchModels: 'getModels',
      resetVehicleOptions: 'resetVehicleOptions',
    }),

    ...mapMutations('trade', {
      setYear: 'SET_VEHICLE_YEAR',
      setMake: 'SET_VEHICLE_MAKE',
      setModel: 'SET_VEHICLE_MODEL',
    }),

    async handleYearSelection(selectedYear) {
      if (!selectedYear) return;
      if (this.isPersistedValue(selectedYear)) return;

      this.setYear(selectedYear);
      await this.resetVehicleOptions(['make', 'model']);
      await this.fetchMakeList();
    },

    async handleMakeSelection(selectedMake) {
      if (!selectedMake) return;
      if (this.isPersistedValue(selectedMake)) return;

      this.setMake(selectedMake);
      await this.resetVehicleOptions(['model']);
      await this.fetchModelList();
    },

    async handleModelSelection(selectedModel) {
      if (!selectedModel) return;
      if (this.isPersistedValue(selectedModel)) return;

      this.setModel(selectedModel);
      await this.resetVehicleOptions(['trim', 'engine']);
    },

    async fetchMakeList() {
      try {
        await this.fetchMakes();
      } catch (error) {
        this.handleSelectionError('Selection Make', error);
      }
    },

    async fetchModelList() {
      try {
        await this.fetchModels();
      } catch (error) {
        this.handleSelectionError('Selection Model', error);
      }
    },

    handleSelectionError(context, error) {
      log(`Error in ${context}`, error);
      this.$router.push({ path: `${this.getProductBasePath()}/error` });
    },

    isPersistedValue(value) {
      return value && typeof value === 'object' && !value.isChanged;
    },
  },
};
</script>
